import { Add as AddIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback } from 'react';
import { ImagePreview } from './ImagePreview';
import type { AttachmentItem, TicketFormData } from './types';

interface SubmitTicketFormProps {
  ticketForm: TicketFormData;
  setTicketForm: React.Dispatch<React.SetStateAction<TicketFormData>>;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

export const SubmitTicketForm: React.FC<SubmitTicketFormProps> = ({
  ticketForm,
  setTicketForm,
  loading,
  onSubmit,
}) => {
  const handleTicketFormChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setTicketForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleTicketFormSelectChange = (e: SelectChangeEvent<string>): void => {
    const { name, value } = e.target;
    setTicketForm((prevState) => ({
      ...prevState,
      [name as string]: value,
    }));
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      const items = e.clipboardData?.items;
      if (!items) {
        return;
      }

      const newAttachments: AttachmentItem[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item?.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            newAttachments.push({ id: crypto.randomUUID(), file });
          }
        }
      }

      if (newAttachments.length > 0) {
        setTicketForm((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...newAttachments],
        }));
      }
    },
    [setTicketForm]
  );

  const handleRemoveAttachment = useCallback(
    (indexToRemove: number) => {
      setTicketForm((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, index) => index !== indexToRemove),
      }));
    },
    [setTicketForm]
  );

  return (
    <Box>
      <Typography>
        Submit a detailed support ticket to track your issue from start to resolution.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        <strong>Tip:</strong> You can take a screenshot using <strong>Windows + Shift + S</strong>{' '}
        (Windows) or <strong>Cmd + Shift + 4</strong> (Mac), and then paste it directly into the
        description field below.
      </Typography>

      <form onSubmit={onSubmit}>
        <TextField
          fullWidth
          label="Subject"
          name="subject"
          value={ticketForm.subject}
          onChange={handleTicketFormChange}
          margin="normal"
          required
          placeholder="Brief description of your issue"
        />

        <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                name="category"
                value={ticketForm.category}
                onChange={handleTicketFormSelectChange}
                label="Category"
              >
                <MenuItem value="technical">Technical Issue</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
                <MenuItem value="feature-request">Feature Request</MenuItem>
                <MenuItem value="general">General Support</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="priority-select-label">Priority</InputLabel>
              <Select
                labelId="priority-select-label"
                id="priority-select"
                name="priority"
                value={ticketForm.priority}
                onChange={handleTicketFormSelectChange}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Description"
          name="description"
          value={ticketForm.description}
          onChange={handleTicketFormChange}
          onPaste={handlePaste}
          margin="normal"
          required
          multiline
          rows={6}
          placeholder="Please provide detailed information about your issue, including steps to reproduce if applicable..."
          slotProps={{
            htmlInput: { maxLength: 5000 },
          }}
          helperText={`${ticketForm.description.length}/5000`}
        />

        {ticketForm.attachments && ticketForm.attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, mb: 2 }}>
            {ticketForm.attachments.map((attachment, index) => (
              <ImagePreview
                key={attachment.id}
                file={attachment.file}
                onRemove={() => handleRemoveAttachment(index)}
              />
            ))}
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          sx={{
            mt: 2,
            backgroundColor: 'bidlogiq.brick',
            color: 'bidlogiq.white',
            '&:hover': {
              backgroundColor: 'bidlogiq.rust',
            },
          }}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {loading ? 'Creating...' : 'Create Ticket'}
        </Button>
      </form>
    </Box>
  );
};
